import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../common/types';
import type { WorkspaceSummary } from '../workspaces/useWorkspaces';
import styles from './TeamAcceptJoinBenefits.module.scss';

interface TeamAcceptJoinBenefitsProps {
  workspaceName: string;
  /** The caller's own workspace (`workspaces.list`), or null before the list loads. */
  homeWorkspace: WorkspaceSummary | null;
}

const bold = (chunks: React.ReactNode[]) => <b>{chunks}</b>;

/** One line with the green check. */
const BenefitLineComponent: React.FC<React.PropsWithChildren> = ({ children }) => (
  <li className="d-flex gap-2 align-items-start">
    <FontAwesomeIcon icon={faCheck} className={`mt-1 ${styles.check}`} aria-hidden="true" />
    <span>{children}</span>
  </li>
);
const BenefitLine = TypedMemo(BenefitLineComponent);

/**
 * design: workspaces c1. What the invitee gets: the own workspace stays, a switch is one click,
 * and there is nothing to pay. The first line names the home workspace when it has artists; else it
 * reads "Your own workspace stays as it is." (the c1 note).
 */
const TeamAcceptJoinBenefitsComponent: React.FC<TeamAcceptJoinBenefitsProps> = ({ workspaceName, homeWorkspace }) => {
  const intl = useIntl();

  const keepOwn =
    homeWorkspace !== null && homeWorkspace.artistCount > 0
      ? intl.formatMessage(
          {
            id: 'app.sml.team.accept.benefit.keepOwnNamed',
            defaultMessage: '<b>You keep your own workspace.</b> {workspace} and its artists stay as they are.',
          },
          { workspace: homeWorkspace.name, b: bold }
        )
      : intl.formatMessage(
          {
            id: 'app.sml.team.accept.benefit.keepOwn',
            defaultMessage: '<b>Your own workspace stays as it is.</b>',
          },
          { b: bold }
        );

  return (
    <ul className={`list-unstyled d-flex flex-column gap-2 small mb-3 p-3 ${styles.benefits}`}>
      <BenefitLine>{keepOwn}</BenefitLine>
      <BenefitLine>
        {intl.formatMessage(
          {
            id: 'app.sml.team.accept.benefit.switch',
            defaultMessage: '<b>Switch any time.</b> Both workspaces sit in the menu at the top of the sidebar.',
          },
          { b: bold }
        )}
      </BenefitLine>
      <BenefitLine>
        {intl.formatMessage(
          {
            id: 'app.sml.team.accept.benefit.nothingToPay',
            defaultMessage: '<b>Nothing to pay.</b> The owner manages billing for {workspace}.',
          },
          { workspace: workspaceName, b: bold }
        )}
      </BenefitLine>
    </ul>
  );
};

export const TeamAcceptJoinBenefits = TypedMemo(TeamAcceptJoinBenefitsComponent);
